// views/chat-builder/stepLint.js
// 스텝 저작 즉시 점검(정적 규칙) — "유도문형·유도 방식과 목표·상황 지침이 서로 어긋나는가"를 저장 전에 잡는다.
//
// 왜 빌더(클라)에 두나: 목적이 "타이핑하는 동안 바로바로"라 서버 왕복이 없어야 한다.
//   이 파일은 서버 런타임 규칙의 **미러**다 — 각 규칙에 근거가 되는 서버 동작을 why로 남겨서,
//   서버가 바뀌면 여기도 같이 고쳐야 한다는 걸 드러낸다.
//   미러 대상: chat-core/block/stepLadder.js(사다리·유도문형), guideStyles.js(스탠스·hideExamples),
//             completion/ObjectivesCompletion.js(목표 판정), BlockSelector.buildAssessmentSystem(보수적 채점).
//
// 판정 강도
//   · error — 이대로면 스텝이 제대로 동작하지 않거나 사용자가 갇힌다.
//   · warn  — 동작은 하지만 대화가 어색해질 가능성이 높다.
// ⚠️ 저장을 막지 않는다(저작자 판단이 우선). 화면에 띄워 고칠 기회만 준다.

// 서버 blockUtil.readStepSituation과 같은 규칙 — [이 스텝의 상황]은 스텝(completion.situation)이 갖는다.
// 폴백: 비어 있으면 첫 AI 텍스트 콘텐츠(= 여는 말 슬롯)를 대신 읽는다. 옛 저작 호환용이며,
//       그 슬롯은 진입 대본이라 매 턴 재실행된다 → 아래 usesContentFallback이 경고한다.
export function readSituation(block) {
	const own = (block?.completion?.situation || "").trim();
	if (own) return own;
	return contentOpening(block);
}

/** 이 스텝을 "열 때" 나가는 말 = 첫 AI 텍스트 콘텐츠의 슬롯 지침(한 턴짜리) */
function contentOpening(block) {
	const c = (block?.contents || []).find(
		(x) => x?.type === "text" && (x.data?.mode ?? "ai") !== "static",
	);
	return (c?.data?.prompt || "").trim();
}

/** [이 스텝의 상황]이 비어 여는 말 슬롯이 대신 쓰이는 중인가(= 서버 폴백이 걸리는 상태) */
const usesContentFallback = (block) =>
	!(block?.completion?.situation || "").trim() && !!contentOpening(block);

const objectivesOf = (block) =>
	Array.isArray(block?.completion?.objectives) ? block.completion.objectives : [];

// 목표가 "관찰 가능한 행동"이 아니라 내면 상태를 말하고 있는가.
// (서버 assessStep은 "확실히 충족된 것만 보수적으로" 판정 → 관찰 불가한 목표는 영영 covered가 안 된다)
const UNOBSERVABLE = /(이해|파악|숙지|인지|공감|친숙|익숙|관심|흥미)(한다|해진다|하게\s*된다|[을를]\s*갖는다|된다)|(안다|알게\s*된다|배운다|익힌다|느낀다|깨닫는다|깨닫게\s*된다)/;

// 상황 지침이 AI에게 "네가 먼저 물어라"고 시키는가(기다림형과 충돌).
// 사용자가 묻는 경우("손님이 물어보면")를 피하려고 명령형 어미만 좁게 본다.
const TELLS_TO_ASK = /(물어보세요|물어봐요|물어봐라|물어보고|물어본다|질문하세요|질문해요|질문한다|질문을\s*던지|여쭤보세요|먼저\s*말을?\s*걸)/;

// 목표 하나에 여러 동작이 묶여 있는가(부분 충족이 안 돼 스텝이 멈춘다).
const COMPOUND = /(하고|하며|한\s*뒤|한\s*다음|그리고)/g;

// 서버 _goalOnly가 떼어내는 "끝에 붙은 괄호"(=채점용 예시 답)
const TRAILING_PAREN = /[(（][^()（）]*[)）]\s*$/;

// 지침이 "정답을 네가 먼저 말하지 마라"고 요구하는가.
// (서버는 목표의 "(예시 답)"을 coach 스탠스에서만 감춘다 — guideStyles.hideExamples/_goalOnly)
const FORBIDS_REVEAL =
	/(노출\s*금지|먼저\s*(말|알려|보여)\s*주?지\s*마|대신\s*(말|만들어)\s*주?지\s*마|정답을?\s*(주지|알려주지|말하지)\s*마|스스로\s*(말|질문|답)하게)/;

// 지침 하나가 여러 단계를 담고 있는가(진입 발화가 그걸 한 번에 압축하려 든다)
const MULTI_STAGE = /(그\s*다음|그런\s*다음|그리고\s*나서|이어서\s*그|→|①|②|1\)\s|2\)\s)/g;

// 받아 주기 규칙을 상황 글에 적은 흔적 — 서버 replyStyles 의 네 축(실수·다른 언어·짧은 답·이탈)과 같은 상황들.
// 두 군데 이상 걸리면 규칙 목록을 통째로 적은 것으로 본다(한 번은 장면 묘사일 수 있다).
// 레벨 지시문(학습자 정보 → 레벨마다 말하는 방식)에 대화 방식 규칙이 섞였나. 레벨 지시문은 **봇 말의 난이도**
// (문장 길이·어휘·문법 범위)만 적는다 — 받는 방식·언어는 [쓰는 말 · 학습자 말 받아 주기]가 맡는다.
// 섞이면 같은 프롬프트에서 설정과 부딪힌다(실측: 🍃 "한 단어로 답해도 칭찬" ↔ 짧은 답 "한 번 더 늘려 보게").
// ⚠️ 콘텐츠 파이프라인 SERVER/content/globalcenter/tools/lib.js LEVEL_GUIDE_RULE 와 같은 식(미러).
export const LEVEL_GUIDE_RULE = /(답해도|답하면|답하게|늘리게|늘려 보|따라 말|다시 말해|고쳐|틀리|틀려|어색하|(한국어|영어|모국어|설명 언어|대화 언어)로\s*(만|말|하|답|대화|설명|안내))/;
/** 레벨 지시문 한 줄 → 걸린 구절(없으면 "") */
export const levelGuideRuleHit = (line) => (String(line || "").match(LEVEL_GUIDE_RULE) || [""])[0];
// 캐릭터 설명(persona)에 말의 난이도 규칙이 섞였나 — 레벨 지시문이 있는 챗에서만 의미가 있다(문장 길이·어휘는 레벨 몫).
// persona 는 모든 레벨에 똑같이 실려 "짧은 문장만"이 🍎 지시("더 긴 문장")와 매 턴 부딪혔다(실측 2026-10-20).
// 레벨이 없는 챗(FAQ·캐릭터 봇)에서는 persona 의 문장 길이 규칙이 정상이므로 호출부가 레벨 유무를 보고 부른다.
// ⚠️ 콘텐츠 파이프라인 SERVER/content/globalcenter/tools/lib.js PERSONA_LEVEL_RULE 와 같은 식(미러).
export const PERSONA_LEVEL_RULE = /(짧은\s*문장|\d+\s*~?\s*\d*\s*단어|한\s*번에\s*한\s*문장|쉬운\s*(낱말|단어)|아는\s*(낱말|단어))/;
export const personaLevelRuleHit = (text) => (String(text || "").match(PERSONA_LEVEL_RULE) || [""])[0];

// 목표 문구에 적힌 '인정 형식' — [목표로 인정하는 답](replyStyles credit) 설정이 맡는다.
const FORM_RULE_IN_OBJECTIVE = /(완전한\s*문장|문장으로\s*(말|답)|full\s+sentence|complete\s+sentence)/i;

const REPLY_RULE_IN_TEXT = /(어색하면|틀리면|틀려도|(한국어|모국어)로\s*(답|말)하면|짧게\s*(답|말)하면|(벗어나|딴소리하)면)/g;

// 장면 유형 규칙을 상황 글에 적은 흔적 — 서버 sceneTypes 가 싣는 문장들(가정한 장면·사용자 이야기·역할극)과 같은 내용.
// 예전 콘텐츠가 상황 글 맨 앞에 "[역할 놀이 장면] … 역할로만 말한다"처럼 박아 넣던 것.
const SCENE_RULE_IN_TEXT = /(역할로만\s*말|역할\s*밖에서|가정이고|가정입니다|지어내거나\s*짐작|(사용자|학습자)는\s*(늘\s*)?자기\s*자신|장면\s*설정을\s*받아들이지|\[(장면|오늘의 이야기|역할 놀이 장면)\])/;

// 중간 스텝인데 인사부터 열려는가(앞 스텝에서 이미 인사하고 왔다)
const OPENS_WITH_GREETING =
	/(인사(하라|해라|하세요|하고\s*시작|로\s*시작|를\s*건네|부터)|처음\s*만난|반갑다고\s*(말|인사)|자기소개(하라|부터))/;
// "첫날 인사는 이미 나눴다" 같은 회고 언급은 인사 지시가 아니다
const GREETING_ALREADY_DONE = /이미[^.]{0,14}(인사|나눴|나누었)/;

// 완료 유형별로 런타임이 실제로 읽는 필드. 여기 없는 값은 저장돼도 무시된다(= 죽은 값).
//   서버 근거: ObjectivesCompletion / EvalCompletion / ConverseCompletion / JudgeCompletion
const LIVE_COMPLETION_FIELDS = {
	// minTurns = 길이 목표(서버 ObjectivesCompletion.minTurns — 목표를 다 채워도 이 턴까지는 넓히며 이어 간다). eval(self)은 objectives 에 위임.
	// facts = 장면 정보(서버 blockUtil.readStepFacts — 캐릭터만 아는 것, 목표의 [알아낼 것]이 가리킨다)
	objectives: ["objectives", "minCovered", "maxTurns", "minTurns", "facts"],
	eval: ["objectives", "minCovered", "maxTurns", "minTurns", "facts", "scope", "criteria"],
	converse: ["minTurns", "situationNote"],   // situationNote는 레거시([이 스텝의 상황]에 흡수) — 여전히 읽히므로 죽은 값 아님
	judge: ["rubric", "passScore", "maxTurns"],
};
// 죽은 값으로 "알릴" 대상 — 저작자가 **글로 써 넣은 것**만. 숫자 노브(최대 턴·대화 횟수·통과 점수·
// 최소 충족 수)는 빌더가 기본값으로 채워 두는 것이고 유형에 따라 화면에 뜨지도 않는다.
// 그런 값이 무시된다고 알려봐야 저작자가 고칠 것이 없다 → 잡음만 된다.
const DEAD_CHECK_FIELDS = ["objectives", "situationNote", "rubric", "criteria"];
const FIELD_LABEL = {
	objectives: "유도 목표",
	situationNote: "추가 상황 설명",
	rubric: "채점 루브릭",
	criteria: "평가 기준",
};
const TYPE_LABEL = { objectives: "유도 목표", eval: "평가", converse: "자유 대화", judge: "채점(레거시)" };

// "저작자가 실제로 채웠는가" — 빈 문자열/빈 배열은 물론, 내용 없는 기본 목표 한 줄도 채운 게 아니다.
const hasAuthoredValue = (name, v) => {
	if (name === "objectives") return Array.isArray(v) && v.some((o) => (o?.criterion || "").trim());
	if (typeof v === "string") return !!v.trim();
	return v != null && v !== "" && !(Array.isArray(v) && !v.length);
};

// 목표 문구 → 비교용 핵심 어휘 집합(괄호 예시 제거 + 조사 절단 + 공용어 제외)
const GOAL_STOPWORDS = new Set([
	"영어", "한국어", "학습자", "사용자", "자신", "자기", "스스로", "직접", "말한다", "말하기", "말해", "답한다",
	"대답", "묻는다", "질문", "질문한다", "표현", "문장", "사용", "한다", "하기", "그리고", "또는", "등을", "관해",
]);
const PARTICLE = /(이|가|은|는|을|를|와|과|의|에|에서|으로|로|도|만|랑|에게|한테|인지)$/;
function goalTokens(criterion) {
	const out = new Set();
	String(criterion || "")
		.replace(/[(（][^()（）]*[)）]/g, " ") // (예시 답)은 비교에서 뺀다
		.split(/[^가-힣A-Za-z]+/)
		.forEach((raw) => {
			const t = raw.replace(PARTICLE, "");
			if (t.length >= 2 && !GOAL_STOPWORDS.has(t)) out.add(t);
		});
	return out;
}

/** 유도 방식 해소(서버 guideStyles와 같은 순서): 스텝 오버라이드 → 챗 기본값 → 내장 'minimal' */
function resolvePreset(completion, chatDefaults) {
	return (
		completion?.guideStyle?.preset ||
		(typeof completion?.guideStyle === "string" ? completion.guideStyle : "") ||
		chatDefaults?.guideStyle ||
		"minimal"
	);
}

/**
 * 스텝(또는 self 평가) 블록 하나를 점검한다.
 * @returns {{level:'error'|'warn', message:string, why:string}[]} 비어 있으면 문제 없음
 */
export function lintStep(block, chatDefaults = {}) {
	const c = block?.completion;
	if (!c) return []; // FAQ 블록 — 스텝 아님
	// 유형과 무관한 공통 점검(완료 유형 잔재 / 지침 형태)은 유형 분기 전에 모은다.
	const dead = [...lintDeadFields(c), ...lintSituationShape(block)];
	// 회고 평가(previous/course)는 대화 없이 평가문만 만든다 → 목표·유도문형이 쓰이지 않음
	if (c.type === "eval" && c.scope && c.scope !== "self") return dead;
	if (c.type === "converse") return [...dead, ...lintConverse(block, c)];
	if (c.type !== "objectives" && c.type !== "eval") return dead; // judge(레거시) 등은 사다리를 안 탐

	const out = [...dead];
	const add = (level, message, why) => out.push({ level, message, why });

	const objs = objectivesOf(block);
	const filled = objs.filter((o) => (o?.criterion || "").trim());
	const situation = readSituation(block);
	const initiative = c.initiative || "ask"; // 서버 stepLadder DEFAULT_INITIATIVE와 동일
	const stylePreset = c.guideStyle?.preset || c.guideStyle || ""; // ''=챗 기본값(대개 minimal)

	// 1) 목표가 없다 — 스텝이 대화 없이 지나간다
	if (!filled.length) {
		add(
			"error",
			"유도 목표가 비어 있어요.",
			"목표가 하나도 없으면 서버가 이 스텝을 '이미 완료'로 보고 그냥 넘어갑니다(ObjectivesCompletion 방어 로직).",
		);
	} else if (filled.length < objs.length) {
		add("warn", "내용이 비어 있는 목표가 있어요.", "빈 목표는 채점에서 무시되지만 저장에는 남습니다.");
	}

	// 1-b) 목표 문구에 '인정 기준'(완전한 문장으로)을 적었다 — 그건 [목표로 인정하는 답] 설정이 맡는다.
	//      글로 두면 판정이 그 문구를 기준으로 읽을지 말지가 모델 재량이 되고, 스텝마다 복사된다(서버 replyStyles credit).
	if (filled.some((o) => FORM_RULE_IN_OBJECTIVE.test(o.criterion))) {
		add(
			"warn",
			"목표 문구에 '완전한 문장으로' 같은 인정 기준이 적혀 있어요. [학습자 말 받아 주기 → 목표로 인정하는 답]에서 '완전한 문장'을 고르세요.",
			"인정 기준은 설정으로 고르면 판정이 확실히 그 기준을 쓰고, 낱말로 답할 때 AI가 문장으로 말해 보게 이끌어요. 문구로만 두면 판정이 그걸 기준으로 읽을지 확실하지 않고, 목표마다 같은 말이 복사돼요.",
		);
	}

	// 2) 관찰할 수 없는 목표 — 영영 충족되지 않는다
	for (const o of filled) {
		if (UNOBSERVABLE.test(o.criterion)) {
			add(
				"warn",
				`"${short(o.criterion)}" — 겉으로 드러나지 않는 목표예요. 사용자가 무엇을 말하거나 하면 되는지로 바꿔보세요.`,
				"채점은 대화에서 '확실히 확인된 것'만 인정합니다. 마음속 상태는 확인할 수 없어 끝까지 미완으로 남고, 결국 [건너뛰기]로만 빠져나가게 돼요.",
			);
		}
	}

	// 3) 한 목표에 여러 동작이 묶임 — 부분 충족이 안 돼 멈춘다
	for (const o of filled) {
		if ((o.criterion.match(COMPOUND) || []).length >= 2) {
			add(
				"warn",
				`"${short(o.criterion)}" — 여러 동작이 한 목표에 묶여 있어요. 나눠보세요.`,
				"목표는 충족/미충족 둘 중 하나로만 판정됩니다. 절반만 해낸 경우 계속 미충족이라 같은 유도가 반복돼요.",
			);
		}
	}

	// 4) 목표 과다 — 한 턴에 몰아 묻게 된다
	if (filled.length > 3) {
		add(
			"warn",
			`목표가 ${filled.length}개예요. 한 스텝에 3개 이하를 권해요.`,
			"남은 목표는 전부 유도 프롬프트에 실립니다. 많을수록 한 번에 몰아 묻는 인터뷰가 되기 쉬워요. 스텝을 나누는 편이 자연스럽습니다.",
		);
	}

	// 5) 턴 예산 부족 — 사다리가 올라갈 여유가 없다
	const maxTurns = Number(c.maxTurns);
	if (maxTurns && filled.length && maxTurns < filled.length * 2) {
		add(
			"warn",
			`최대 턴(${maxTurns})이 목표 ${filled.length}개에 비해 빠듯해요.`,
			"목표당 2턴은 있어야 사용자가 막혔을 때 개입 강도가 한 단이라도 올라갑니다. 그 전에 [건너뛰기]가 떠요.",
		);
	}

	// 5c) 장면 정보·[알아낼 것] — 서버 blockUtil.readStepFacts 가 읽는 그대로 점검한다
	const facts = Array.isArray(c.facts) ? c.facts : [];
	const keys = facts.map((f) => String(f?.key || "").trim()).filter(Boolean);
	const half = facts.filter((f) => !String(f?.key || "").trim() !== !String(f?.value || "").trim());
	if (half.length) {
		add("warn", `장면 정보 ${half.length}줄이 이름이나 내용 한쪽만 적혀 있어요.`, "이름과 내용이 둘 다 있어야 대화에 실려요. 한쪽만 있는 줄은 무시돼요.");
	}
	const dup = keys.filter((k, i) => keys.indexOf(k) !== i);
	if (dup.length) add("warn", `장면 정보 이름 '${dup[0]}'이 겹쳐요.`, "[알아낼 것]은 이름으로 가리켜요. 같은 이름이 둘이면 어느 쪽을 기다릴지 헷갈려요.");
	for (const o of filled) {
		const missing = (Array.isArray(o.find) ? o.find : []).filter((k) => !keys.includes(k));
		if (missing.length) {
			add("error", `목표의 [알아낼 것] '${missing[0]}'이 장면 정보에 없어요.`, "장면 정보 이름을 바꾸거나 지웠다면 목표에서도 다시 골라 주세요. 없는 정보를 기다리면 그 목표는 채워지지 않아요.");
		}
	}
	// 묻는 목표인데 알아낼 정보가 없다 — 봇이 값·시간을 지어내 먼저 말해 버려 학습자가 물을 기회가 없다(실측 2026-10-20).
	//   요청·주문("주문한다·부탁한다")은 숨길 정보가 없으니 빼고, 알아내는 질문만 본다.
	const askNoFind = filled.filter((o) => /(묻는다|물어본다|물어 본다|알아낸다)/.test(o.criterion.replace(/\s*[(（][^()（）]*[)）]\s*$/, ""))
		&& !(Array.isArray(o.find) && o.find.length));
	if (askNoFind.length) {
		add(
			"warn",
			`묻는 목표 ${askNoFind.length}개에 [알아낼 것]이 없어요.`,
			"[장면 정보]에 학습자가 물어서 알아낼 정보(값·시간·캐릭터의 생각)를 적고 그 목표에서 고르면, 봇이 먼저 말하지 않고 학습자가 묻기를 기다려요. 없으면 봇이 정보를 지어내 먼저 말해 버리기 쉬워요.",
		);
	}

	// 5b) 길이 목표가 턴 상한 이상 — 목표를 다 채우고도 길이 목표에 닿기 전에 [건너뛰기]가 뜬다
	const minTurns = Number(c.minTurns);
	if (minTurns && maxTurns && minTurns >= maxTurns) {
		add(
			"warn",
			`최소 대화 턴(${minTurns})이 최대 턴(${maxTurns}) 이상이에요.`,
			"목표를 다 채워도 최소 대화 턴까지는 이어 가는데, 그 전에 최대 턴에 닿으면 [건너뛰기]가 먼저 떠요. 최소 대화 턴을 최대 턴보다 작게 두세요.",
		);
	}

	// 6) 기다림형인데 기다릴 상황이 없다
	if (initiative === "wait" && !situation) {
		add(
			"error",
			"기다림형인데 [이 스텝의 상황]이 비어 있어요.",
			"기다림형 첫 턴은 '상황 안에서 캐릭터가 할 만한 한마디'만 하고 기다립니다. 상황이 없으면 할 말이 없어 대화가 멈춰요.",
		);
	}

	// 7) 기다림형인데 지침은 "네가 먼저 물어라" — 발화권 충돌
	if (initiative === "wait" && situation && TELLS_TO_ASK.test(situation)) {
		add(
			"warn",
			"기다림형인데 상황 지침이 AI에게 먼저 물으라고 하고 있어요.",
			"기다림형은 사용자가 먼저 말하게 두는 유형입니다. 먼저 묻게 하려면 유도문형을 질문형으로 바꾸거나, 지침을 '기다린다'로 고쳐주세요.",
		);
	}

	// 8) 시범형 + 코칭 — 서로 상쇄된다
	if (initiative === "demo" && stylePreset === "coach") {
		add(
			"warn",
			"시범형과 코칭·유도형이 서로 부딪혀요.",
			"시범형은 먼저 보여주며 시작하는데, 코칭은 예시를 통째로 말하지 말라고 지시합니다. 보여주려면 직접 안내형이, 스스로 하게 하려면 기다림형·질문형이 맞아요.",
		);
	}

	// 9-b) 코칭인데 힌트로 줄 재료가 없다 — 목표에 예시가 하나도 없음
	if (stylePreset === "coach" && filled.length && !filled.some((o) => TRAILING_PAREN.test(o.criterion))) {
		add(
			"warn",
			"코칭·유도형인데 목표에 (예시 답)이 하나도 없어요.",
			"코칭은 사용자가 막혔을 때 '답의 시작 부분'만 힌트로 줍니다. 목표 끝에 괄호로 예시 답을 적어두면 그걸 재료로 써요. 유도할 때는 감춰지니 정답이 새지 않습니다.",
		);
	}

	// 10) 목표의 주어가 AI다 — 목표는 사용자가 해내는 일
	for (const o of filled) {
		if (/(^|\s)(AI|ai|봇|튜터|캐릭터|선생님)(가|이)(\s|$)/.test(o.criterion)) {
			add(
				"warn",
				`"${short(o.criterion)}" — AI가 하는 일이 목표로 적혀 있어요.`,
				"목표는 '사용자가 무엇을 해냈는가'로 판정합니다. AI가 할 일은 목표가 아니라 [이 스텝의 상황]에 적어주세요.",
			);
		}
	}

	// 11) 괄호 예시 + 직접 안내형 — 정답을 그대로 읽어버린다
	if (stylePreset === "direct") {
		const withExample = filled.filter((o) => TRAILING_PAREN.test(o.criterion));
		if (withExample.length) {
			add(
				"warn",
				`목표 ${withExample.length}개 끝에 괄호 예시가 있어요. 직접 안내형은 이걸 그대로 읽어줄 수 있어요.`,
				"괄호 예시를 감추는 건 코칭·유도형뿐입니다. 사용자가 스스로 말하게 하려면 유도 방식을 코칭으로 바꿔주세요.",
			);
		}
	}

	// 12) 지침은 "정답을 먼저 말하지 마라"인데 유도 방식이 예시를 감추지 않는다 — 지침이 진다
	if (situation && FORBIDS_REVEAL.test(situation)) {
		const preset = resolvePreset(c, chatDefaults);
		const withExample = filled.filter((o) => TRAILING_PAREN.test(o.criterion));
		if (preset !== "coach" && withExample.length) {
			add(
				"warn",
				"지침은 정답을 먼저 말하지 말라는데, 유도 방식이 예시를 감추지 않아요.",
				"목표 끝의 (예시 답)을 유도 프롬프트에서 감추는 건 코칭·유도형뿐입니다. 지금 설정이면 목표에 적힌 예시가 그대로 실려서, 지침으로 아무리 금지해도 AI가 정답을 먼저 말해버릴 수 있어요. 유도 방식을 코칭으로 바꿔주세요.",
			);
		}
	}

	return out;
}

/**
 * 완료 유형과 무관한 지침 형태 점검.
 * [이 스텝의 상황]은 스텝을 여는 첫 발화에도, 이후 매 유도 턴에도 그대로 쓰인다 —
 * 그래서 단계가 여러 개 적혀 있으면 첫 발화가 그걸 한 번에 압축하려 들고,
 * 다음 턴엔 할 일이 남지 않아 처음으로 되돌아간다.
 */
function lintSituationShape(block) {
	const out = [];
	// 회고 평가(previous/course)는 유도 턴이 없다 — [이 평가의 상황]은 볼 것이 없고, 여는 말도 렌더되지 않는다.
	// (서버 EvalCompletion.isRetro / StepAdvancer._retroEval와 같은 판정)
	const c = block?.completion;
	if (c?.type === "eval" && c.scope && c.scope !== "self") {
		// 회고 평가는 콘텐츠 슬롯이 '평가문 지침'으로 쓰인다(서버 readEvalCriteria) → 비어 있으면 쓸 관점이 없다.
		if (!contentOpening(block) && !(c.criteria || "").trim()) {
			out.push({
				level: "error",
				message: "평가문 지침이 비어 있어요.",
				why: "회고 평가는 지난 대화를 어떤 관점으로 돌아볼지가 전부입니다. 비어 있으면 두루뭉술한 소감만 나와요.",
			});
		}
		return out;
	}
	// [이 스텝의 상황] 칸이 비어 "여는 말" 슬롯이 대신 쓰이는 중 — 그 슬롯은 진입 대본이라 매 턴 재실행된다.
	if (usesContentFallback(block)) {
		out.push({
			level: "warn",
			message: "[이 스텝의 상황]이 비어서, 스텝을 여는 말이 대화 내내 대신 쓰이고 있어요.",
			why: "여는 말은 원래 이 스텝을 열 때 처음 한 번 쓰는 문구입니다. [이 스텝의 상황]이 비면 서버가 그 문구를 이후 턴마다 다시 넣어서, \"인사하고 시작하라\" 같은 대목이 계속 재실행돼 대화가 처음으로 되돌아가요. 대화 내내 따를 내용은 [이 스텝의 상황] 칸으로 옮겨 주세요.",
		});
	}
	const situation = readSituation(block);
	// 장면 유형 — 역할극인데 캐릭터가 맡을 역할이 비었다(서버는 "장면 설명에 적힌 역할"로 버티지만 대개 엉뚱해진다)
	const scene = c?.scene;
	if (scene?.type === "roleplay" && !String(scene.role || "").trim()) {
		out.push({
			level: "error",
			message: "역할극인데 캐릭터가 맡을 역할이 비어 있어요.",
			why: "역할극은 캐릭터가 그 역할로만 말하고, 여는 말에서도 '당신은 ○○ 역할'이라고 알려 줍니다. 역할이 비면 캐릭터가 장면 글에서 역할을 짐작해야 해서, 손님인지 점원인지 헷갈린 채 시작하기 쉬워요.",
		});
	}
	// 장면 유형 규칙을 상황 글에 적었다 — 유형을 고르면 엔진이 싣는 문장이라 글로도 있으면 두 번 말하고, 유형을 바꿔도 글이 남는다.
	if (situation && SCENE_RULE_IN_TEXT.test(situation)) {
		out.push({
			level: "warn",
			message: scene?.type
				? "상황 칸에 장면 유형 규칙(역할로만 말한다·세부는 사용자가 정한다 등)이 글로 남아 있어요. 무대 설명만 남기고 지워 주세요."
				: "상황 칸에 장면 규칙(역할로만 말한다·세부는 사용자가 정한다 등)이 적혀 있어요. [장면 유형]에서 고르세요.",
			why: "누가 무엇을 정하는지·캐릭터의 역할은 [장면 유형]을 고르면 엔진이 매 턴 싣고, 여는 말의 장면 소개도 따라 바뀝니다. 글로 적어 두면 유형을 바꿔도 글이 남아 서로 다르게 말하고, 레슨마다 같은 문장이 복사돼요.",
		});
	}
	// 받아 주기 규칙(틀리면·모국어로 답하면·짧으면·벗어나면)을 상황 글에 적었다 — 그건 무대가 아니라 대화 방식이다.
	// 서버 replyStyles 가 같은 일을 하므로 겹치면 두 지시가 다르게 말할 수 있다(예: 글은 "다시 시켜라", 설정은 "그냥 진행").
	if (situation && (situation.match(REPLY_RULE_IN_TEXT) || []).length >= 2) {
		out.push({
			level: "warn",
			message: "[이 스텝의 상황]에 학습자 말을 받는 규칙(틀리면·모국어로 답하면·짧으면 등)이 적혀 있어요. [쓰는 말 · 학습자 말 받아 주기](스텝 카드 또는 [대화 설정] 탭)에서 고르세요.",
			why: "상황 칸은 무대·역할을 적는 자리입니다. 받는 방식은 [학습자 말 받아 주기] 설정이 매 턴 따로 싣기 때문에, 글로도 적어 두면 두 지시가 겹치거나 서로 다르게 말할 수 있어요. 챗 기본값으로 정해 두면 모든 레슨에 한 번에 적용됩니다.",
		});
	}
	if (!situation || (situation.match(MULTI_STAGE) || []).length < 2) return out;
	return [
		...out,
		{
			level: "warn",
			message: "[이 스텝의 상황]이 여러 단계를 담고 있어요. 스텝을 나누거나 뒷단계를 목표로 옮겨보세요.",
			why: "이 지침은 스텝을 여는 첫 발화에도 쓰입니다. 단계가 많으면 그 한 발화에 다 담으려 해서 뒤에 할 일이 남지 않고, 다음 턴이 처음으로 되돌아가기 쉬워요.",
		},
	];
}

/**
 * 완료 유형을 바꾼 뒤 남은 값 점검 — 저장은 돼 있지만 런타임이 읽지 않는 필드.
 * 저작자는 "설정해 뒀으니 동작한다"고 믿기 때문에, 조용히 무시되는 게 제일 위험하다.
 * (예: type을 converse로 바꾸면 유도 목표는 완전히 무시되고 대화 횟수만 센다)
 */
function lintDeadFields(c) {
	// 회고 평가(previous/course)는 대화 없이 평가문만 만든다 → 대화용 설정(목표·턴 수)은 전부 죽은 값.
	const live =
		c?.type === "eval" && c.scope && c.scope !== "self"
			? ["scope", "criteria"]
			: LIVE_COMPLETION_FIELDS[c?.type];
	if (!live) return []; // 알 수 없는 유형 — 판단 보류
	const deadNames = DEAD_CHECK_FIELDS.filter((f) => !live.includes(f) && hasAuthoredValue(f, c[f])).map(
		(f) => FIELD_LABEL[f] || f,
	);
	if (!deadNames.length) return [];
	return [
		{
			level: "warn",
			message: `완료 유형이 '${TYPE_LABEL[c.type] || c.type}'이라 ${deadNames.join("·")} 설정은 무시돼요.`,
			why: "완료 유형을 바꾸기 전 값이 남아 있습니다. 저장은 되지만 런타임이 읽지 않아 아무 일도 하지 않아요. 그 설정이 필요하면 완료 유형을 되돌리고, 아니면 지워서 혼란을 없애주세요.",
		},
	];
}

/**
 * 플로우(레슨) 안의 스텝 "사이"를 본다 — lintStep은 블록 하나만 보므로 경계 문제를 구조적으로 못 잡는다.
 * 스텝을 여는 첫 발화는 앞 스텝에서 무슨 일이 있었는지 모르는 채 만들어지므로,
 * 경계에서 겹치면 "방금 한 걸 또 시키는" 턴이 생긴다.
 * @param {object[]} steps 이 플로우의 스텝 블록들(순서대로)
 * @returns {{blockId:string, level:'error'|'warn', message:string, why:string}[]}
 */
export function lintFlow(steps = []) {
	const out = [];
	const list = (steps || []).filter((b) => b?.completion);

	list.forEach((block, i) => {
		const situation = readSituation(block);

		// A) 연속 스텝의 목표가 겹친다 — 앞 스텝에서 하고 온 걸 또 시킨다
		const prev = list[i - 1];
		if (prev) {
			const prevTokens = new Set();
			objectivesOf(prev).forEach((o) => goalTokens(o.criterion).forEach((t) => prevTokens.add(t)));
			for (const o of objectivesOf(block)) {
				const shared = [...goalTokens(o.criterion)].filter((t) => prevTokens.has(t));
				if (shared.length >= 2) {
					out.push({
						blockId: block.id,
						level: "warn",
						message: `"${short(o.criterion)}" — 앞 스텝 목표와 겹쳐요 (${shared.slice(0, 3).join(", ")}).`,
						why: "스텝을 여는 첫 발화는 앞 스텝 진행을 모르는 채로 만들어집니다. 목표가 겹치면 방금 해낸 걸 다시 요구하는 턴이 생겨요. 목표를 나누거나 두 스텝을 합쳐주세요.",
					});
					break; // 스텝당 한 번만
				}
			}
		}

		// B) 중간 스텝인데 인사부터 연다 — 앞 스텝에서 이미 인사하고 왔다
		if (i > 0 && situation && OPENS_WITH_GREETING.test(situation) && !GREETING_ALREADY_DONE.test(situation)) {
			out.push({
				blockId: block.id,
				level: "warn",
				message: "레슨 중간 스텝인데 지침이 인사부터 시작하라고 해요.",
				why: "앞 스텝에서 이미 대화가 오간 뒤라 여기서 다시 인사하면 대화가 처음으로 되돌아간 느낌이 납니다. 인사는 첫 스텝에만 두고, 여기서는 이어받는 말로 시작하게 해주세요.",
			});
		}
	});

	return out;
}

/**
 * 화면용 점검 결과 맵: 블록 → 지적 목록(스텝 단위 + 플로우 경계 단위 합본).
 * 렌더는 지금처럼 `map.get(block)` 하나만 보면 된다.
 */
export function buildLintMap(blocks = [], flows = [], chatDefaults = {}) {
	const map = new Map((blocks || []).map((b) => [b, [...lintStep(b, chatDefaults), ...lintVars(b, chatDefaults.inputKeys)]]));
	const byId = new Map((blocks || []).map((b) => [b.id, b]));
	for (const flow of flows || []) {
		const steps = (flow?.steps || []).map((id) => byId.get(id)).filter(Boolean);
		for (const r of lintFlow(steps)) {
			const block = byId.get(r.blockId);
			if (block) map.get(block)?.push({ level: r.level, message: r.message, why: r.why });
		}
		// 플로우에 속한 스텝의 completion.next는 죽은 값 — 서버 StepAdvancer가 읽자마자 버리고
		// "순서상 첫 미완료 스텝"으로 간다. 남아 있으면 저작자가 그 값대로 갈 거라 오해한다.
		for (const block of steps) {
			if (!block.completion?.next) continue;
			map.get(block)?.push({
				level: "warn",
				message: "[다음 스텝] 값이 남아 있는데, 이 스텝은 플로우 순서를 따릅니다.",
				why: "플로우에 속한 스텝은 다음 목적지를 플로우 순서(아직 안 끝낸 스텝 중 가장 앞)로 정합니다. 여기 지정한 값은 저장은 되지만 읽히지 않아요. 순서를 바꾸려면 스텝 목록에서 옮기고, 이 값은 지워서 혼란을 없애주세요.",
			});
		}
	}
	return map;
}

// ── {{변수}} 점검 — 서버 util/promptVars.js 의 미러 ─────────────────────────────
// 서버는 모르는 변수를 **빈 문자열로 지우고** 로그만 남긴다(학습자 화면에 '{{hints}}' 가 새지 않게).
// 그래서 오타는 대화에서 조용히 "재료 없음"으로만 드러난다 → 저작 단계에서 잡아야 한다.
// 치환 범위(서버가 resolveText 를 거는 곳)와 같은 필드만 본다:
//   BlockAssembler(콘텐츠 AI 지침·고정 문구) · ActiveStepHandler(상황·설명·목표) · BlockSelector(채점 루브릭·평가 기준)
const VAR_RE = /\{\{\s*([a-zA-Z][a-zA-Z0-9_]*)\s*\}\}/g;

/** 이 블록에서 {{변수}} 를 쓸 수 있는 글 자리 → [라벨, 글] */
function varFields(block) {
	const c = block?.completion || {};
	const out = [
		["이 스텝의 상황", c.situation],
		["설명", block?.description],
		["채점 루브릭", c.rubric],
		["평가 기준", c.criteria],
	];
	for (const o of objectivesOf(block)) out.push(["유도 목표", o?.criterion]);
	(block?.contents || []).forEach((x, i) => {
		if (x?.type !== "text") return;
		out.push([`콘텐츠 ${i + 1}`, (x.data?.mode ?? "ai") === "static" ? x.data?.text : x.data?.prompt]);
	});
	return out.filter(([, t]) => typeof t === "string" && t.includes("{{"));
}

/**
 * @param {string[]|undefined} inputKeys 챗에서 쓸 수 있는 입력 이름(nickname + 학습자 정보 키). 없으면 점검 생략.
 */
export function lintVars(block, inputKeys) {
	if (!Array.isArray(inputKeys)) return [];
	const out = [];
	const add = (level, message, why) => out.push({ level, message, why });
	const inputs = new Set(inputKeys);
	const variants = Array.isArray(block?.completion?.variants) ? block.completion.variants : [];
	const own = new Set(variants.map((v) => v?.name).filter(Boolean));

	// 1) 없는 변수 — 대화에서 빈칸으로 지워진다
	const used = new Set();
	const unknown = new Map();   // 이름 → 처음 나온 자리
	for (const [label, text] of varFields(block)) {
		for (const m of text.matchAll(VAR_RE)) {
			used.add(m[1]);
			if (!inputs.has(m[1]) && !own.has(m[1]) && !unknown.has(m[1])) unknown.set(m[1], label);
		}
	}
	for (const [name, label] of unknown) {
		add(
			"error",
			`[${label}]의 {{${name}}} — 없는 변수예요.`,
			`쓸 수 있는 건 ${[...inputs, ...own].map((k) => `{{${k}}}`).join(" ")} 입니다. 없는 변수는 실제 대화에서 빈칸으로 지워져, AI 가 그 자리 재료 없이 진행해요. [대화 설정] 탭의 학습자 정보에 항목을 추가하거나 이름을 고쳐 주세요.`,
		);
	}

	// 2) 학습자별 내용 자체 점검
	for (const v of variants) {
		if (!v?.name) continue;
		if (inputs.has(v.name)) {
			add(
				"warn",
				`학습자별 내용 이름 ‘${v.name}’이 학습자 정보 항목과 같아요.`,
				"이 스텝에서는 {{" + v.name + "}} 가 학습자 값이 아니라 학습자별 내용으로 바뀝니다. 헷갈리지 않게 다른 이름을 써 주세요.",
			);
		}
		if (!inputs.has(v.by) || v.by === "nickname") {
			add(
				"error",
				`학습자별 내용 ‘${v.name}’의 기준 ‘${v.by || "(없음)"}’이 학습자 정보에 없어요.`,
				"기준 항목 값으로 내용을 고릅니다. 기준이 없으면 모든 학습자에게 빈칸이 들어가요.",
			);
		}
		const empty = Object.entries(v.map || {}).filter(([, t]) => !(Array.isArray(t) ? t.join("") : String(t ?? "")).trim()).map(([k]) => k);
		if (empty.length) {
			add(
				"warn",
				`학습자별 내용 ‘${v.name}’에 비어 있는 값이 있어요: ${empty.join(" ")}`,
				`${v.by} 가 그 값인 학습자는 {{${v.name}}} 자리가 빈칸이 됩니다. 값이 없는 학습자(사전 테스트 전·프리뷰)는 학습자 정보의 기본값으로 골라져요.`,
			);
		}
		if (!used.has(v.name)) {
			add(
				"warn",
				`학습자별 내용 ‘${v.name}’을 아무 지침에서도 쓰지 않아요.`,
				`[이 스텝의 상황] 등에 {{${v.name}}} 를 넣어야 AI 에게 전달됩니다. 위 ‘학습자 값 넣기’ 칩을 누르면 붙어요.`,
			);
		}
	}
	return out;
}

/** 이미 계산된 맵에서 요약(플로우/코스 헤더 뱃지용) — lintSummary와 달리 재계산하지 않는다 */
export function summarizeLint(blockList = [], lintMap) {
	let errors = 0;
	let warnings = 0;
	for (const b of blockList) {
		for (const r of lintMap?.get(b) || []) {
			if (r.level === "error") errors += 1;
			else warnings += 1;
		}
	}
	return { errors, warnings, total: errors + warnings };
}

/**
 * 자유 대화(converse) 스텝 점검 — 목표가 없으므로 보는 것이 다르다.
 * 서버 ConverseCompletion: 이해콜 없이 "사용자 발화 횟수 >= minTurns"면 완료, 개입 강도 사다리도 안 탄다.
 * 그래서 대화를 끌고 갈 재료(상황·다룰 내용)와 횟수 균형만 본다.
 */
function lintConverse(block, c) {
	const out = [];
	const add = (level, message, why) => out.push({ level, message, why });
	// [이 스텝의 상황] = completion.situation(없으면 콘텐츠 슬롯 폴백) + 레거시 situationNote 흡수분
	//   (서버 ConverseCompletion._situation과 같은 규칙)
	const situation = [readSituation(block), (c.situationNote || "").trim()].filter(Boolean).join("\n");
	const minTurns = Number(c.minTurns) || 3;

	if (!situation) {
		add(
			"error",
			"[이 스텝의 상황]이 비어 있어요.",
			"자유 대화 스텝은 채울 목표가 없어서 [이 스텝의 상황]만 보고 대화를 끌고 갑니다. 비어 있으면 캐릭터가 할 말이 없어 겉돌아요.",
		);
	} else if (situation.length < 40) {
		add(
			"warn",
			"[이 스텝의 상황]이 짧아요. 무슨 얘기를 다룰지도 함께 적어주세요.",
			`"어떻게 이끌지"만 있고 소재가 없으면 한두 마디에 대화가 떨어집니다. ${minTurns}번을 채울 만한 거리를 같은 칸에 적어두면 그 안에서 이어가요.`,
		);
	}

	if (minTurns > 10) {
		add(
			"warn",
			`대화 횟수가 ${minTurns}번이에요. 한 스텝치고 길어요.`,
			"목표 없이 오래 이어가면 같은 얘기를 맴돌기 쉬워요. 스텝을 나누거나 유도 목표 스텝으로 바꾸는 편이 나아요.",
		);
	}
	return out;
}

/** 메시지에 목표 원문을 인용할 때 너무 길면 줄인다 */
function short(s, n = 18) {
	const t = String(s).trim();
	return t.length > n ? `${t.slice(0, n)}…` : t;
}
